import { elementAttributes } from "../html-attributes";
import { modelsAlignment, parseTextBlockStyleAttrs } from "../html-parse";
import { EMPTY_STYLE_MAP, type StyleMap } from "../shared-block-styles";
import { EmailBlockDefinition } from "../types";

import { LuHeading1 } from "react-icons/lu";

import { HeadingEmailRenderer } from "./email-renderer";
import { HeadingTiptapView } from "./tiptap-view";

export interface HeadingAttrs extends Record<string, unknown> {
  level: 1 | 2 | 3;
  textAlign: "left" | "center" | "right";
  /** Inline styles as the markup wrote them — the block's style store. */
  style: StyleMap;
  /** Attributes the block does not otherwise model, kept rather than dropped. */
  attributes: Record<string, string>;
  /** Named style overrides carried by documents saved before `style` existed. */
  backgroundColor: string;
  fontFamily: string;
  fontSize: string;
  color: string;
  fontWeight: string;
  lineHeight: string;
  textDecoration: string;
  borderRadius: string;
  borderWidth: string;
  borderStyle: string;
  borderColor: string;
  paddingTop: string;
  paddingBottom: string;
  paddingLeft: string;
  paddingRight: string;
  marginTop: string;
  marginBottom: string;
}

export const headingBlock: EmailBlockDefinition<HeadingAttrs> = {
  name: "emailHeading",
  label: "Heading",
  icon: LuHeading1,
  category: "content",

  schema: {
    attrs: {
      level: { default: 1 },
      textAlign: { default: "left" },
      style: { default: EMPTY_STYLE_MAP },
      attributes: { default: {} },
      backgroundColor: { default: "" },
      fontFamily: { default: "" },
      fontSize: { default: "" },
      color: { default: "" },
      fontWeight: { default: "" },
      lineHeight: { default: "" },
      textDecoration: { default: "" },
      borderRadius: { default: "" },
      borderWidth: { default: "" },
      borderStyle: { default: "" },
      borderColor: { default: "" },
      paddingTop: { default: "" },
      paddingBottom: { default: "" },
      paddingLeft: { default: "" },
      paddingRight: { default: "" },
      marginTop: { default: "" },
      marginBottom: { default: "" },
    },
    content: "inline*",
    group: "emailBlock",
    draggable: true,
  },

  // Three levels is the whole vocabulary, so `h4`-`h6` are declined rather than
  // clamped: an `<h4>` rendered as an `<h3>` is a different heading, and the
  // fallback tier re-emits it as the one the designer wrote.
  parseHTML: () =>
    [1, 2, 3].map((tagLevel) => ({
      tag: `h${tagLevel}`,
      getAttrs: (el: HTMLElement) => {
        if (!modelsAlignment(el)) return false;
        return {
          ...parseTextBlockStyleAttrs(el),
          attributes: elementAttributes(el, ["align"]),
          level: tagLevel,
        };
      },
    })),

  tiptapComponent: HeadingTiptapView,
  emailRenderer: HeadingEmailRenderer,
};
